// Call card template command — creates a pre-filled call card from a CT command
// Format: ct;w1;<call sign>;<location> or ct;w2;<call sign>;<location>

import { getCurrentCompanyId } from '@/utils/auth';
import { getAvailableCADOfficers, listNatureOfCall, listCallDispositions, saveCallCard, closeCallCard } from '@/services/api';
import { processCallCardAvailableOfficers } from '@/utils/call-card-helpers';
import { validateAddressWithSuggestions } from '@/utils/custom-location';
import { createCallCard } from '@/models/call-card';
import { DataObjectLocationFields } from '@/models/legacy/index';
import { load } from '@/services/loading';
import { App } from '@/services/app-compat';

interface CallCardTemplateConfig {
    nocCode: number;
    w1DispositionCode: number;
    w2DispositionCode: number;
}

const COMPANY_CONFIGS: Record<number, CallCardTemplateConfig> = {
    2: { nocCode: 407, w1DispositionCode: 7, w2DispositionCode: 8 },
    816: { nocCode: 3206, w1DispositionCode: 314, w2DispositionCode: 315 },
    928: { nocCode: 3049, w1DispositionCode: 309, w2DispositionCode: 310 },
};

export function getCallCardTemplateConfig(): CallCardTemplateConfig | null {
    const companyId = getCurrentCompanyId();
    return COMPANY_CONFIGS[companyId] ?? null;
}

export function isCallCardTemplateCommand(command: string): boolean {
    const parts = command.split(';');
    if (parts.length !== 4) return false;
    const prefix = parts[0].trim().toUpperCase();
    const type = parts[1].trim().toUpperCase();
    return prefix === 'CT' && (type === 'W1' || type === 'W2');
}

export async function executeCallCardTemplateCommand(command: string, config: CallCardTemplateConfig): Promise<void> {
    const parts = command.split(';');
    const type = parts[1].trim().toUpperCase() as 'W1' | 'W2';
    const callSign = parts[2].trim();
    const location = parts[3].trim();

    if (!callSign) {
        App.showPopupNotification('Call sign is required.', 'error');
        return;
    }
    if (!location) {
        App.showPopupNotification('Location is required.', 'error');
        return;
    }

    load.showLoadingScreen();

    try {
        const [officersResult, nocResult, dispositionsResult] = await Promise.all([
            getAvailableCADOfficers(null as any),
            listNatureOfCall(),
            listCallDispositions(),
        ]);

        processCallCardAvailableOfficers(officersResult);

        // Find officer by call sign in name field: "Last, First (CALLSIGN)"
        const officer = officersResult.officerList.find((o: any) => {
            const match = o.name?.match(/\(([^)]+)\)/);
            if (!match) return false;
            const officerCallSign = match[1].split(',')[0].trim();
            return officerCallSign.toUpperCase() === callSign.toUpperCase();
        });

        if (!officer) {
            App.showPopupNotification(`Officer with call sign "${callSign}" not found.`, 'error');
            load.hideLoadingScreen();
            return;
        }

        // Find NOC and disposition
        const nocItem = nocResult.natureOfCallList.find((n: any) => n.code === config.nocCode);
        const dispositionCode = type === 'W1' ? config.w1DispositionCode : config.w2DispositionCode;
        const dispositionItem = dispositionsResult.callDispositionList.find((d: any) => d.dispositionCode === dispositionCode);

        if (!nocItem) {
            App.showPopupNotification(`Nature of call code ${config.nocCode} not found.`, 'error');
            load.hideLoadingScreen();
            return;
        }
        if (!dispositionItem) {
            App.showPopupNotification(`Disposition code ${dispositionCode} not found.`, 'error');
            load.hideLoadingScreen();
            return;
        }

        // Geovalidate address
        const locationResultObj = await validateAddressWithSuggestions({
            address: location,
            isLocationRequired: true,
            showLoadingScreen: false,
            // Call entry: accept low-confidence results without a preview; a template command
            // must never block on a modal. companyId lets a not-found address fall back to the
            // company's operating-area centre instead of failing.
            acceptApproximate: true,
            companyId: getCurrentCompanyId(),
        });

        // Build call card — set client to null to avoid "Client is invalid" (default has id: 0)
        const now = new Date().toISOString();
        const callCard = createCallCard({
            client: null as any,
            callDateTime: now,
            natureOfCall: [nocItem.code],
            natureOfCallValues: [nocItem.value],
            callDisposition: dispositionItem.dispositionCode,
            callDispositionValue: dispositionItem.dispositionValue,
            primaryOfficer: officer.id,
            primaryOfficerName: officer.name,
            primaryDispatched: now,
            primaryEnroute: now,
            primaryOnScene: now,
            primaryCleared: now,
        });

        // Fill in address from geo results
        const fillInAddressFromSuggestions = (window as any).fillInAddressFromSuggestions;
        if (fillInAddressFromSuggestions) {
            fillInAddressFromSuggestions(locationResultObj, callCard, null, DataObjectLocationFields.callForService, false);
        }

        // Save then close
        const saveResult = await saveCallCard(callCard);
        callCard.id = saveResult.id;
        await closeCallCard(callCard);

        App.showPopupNotification(`Call card created and closed for ${callSign}.`, 'success');
    } catch (e: any) {
        const message = e?.message || 'Unknown error';
        // A cancelled address confirmation (legacy picker or the Verify Address modal) is a normal
        // exit, not a failure — don't surface the internal reason code as an error.
        const cancelled = message === 'address-selection-cancelled'
            || message === 'preview-cancelled'
            || message === 'drop-pin-cancelled'
            || message === 'keep-previous-location';
        if (!cancelled) {
            App.showPopupNotification(`Template command failed: ${message}`, 'error');
        }
    } finally {
        load.hideLoadingScreen();
    }
}
